import moment from "moment";

import type {
  ApprovalGroup,
  ApprovalStep,
  GuardrailEvaluation,
} from "src/components/RequisitionGuardrailOutcome";

import { failingCheckLabels } from "./item";

export const formatWorkspaceStamp = (
  at: string | null | undefined,
  kind: "date" | "datetime",
  formats: { readable: string; readableDateTime: string }
): string | null => {
  const value = at?.trim() ?? "";
  if (value === "") {
    return null;
  }
  const parsed =
    kind === "date" ? moment(value, "YYYY-MM-DD", true) : moment(value);
  if (!parsed.isValid()) {
    return null;
  }
  if (kind === "date") {
    return parsed.format(formats.readable);
  }
  return parsed.local().format(formats.readableDateTime);
};

export type HistoryIcon =
  | "file"
  | "clipboard-check"
  | "paper-plane"
  | "comment"
  | "check"
  | "xmark"
  | "clock";

export type HistoryRow = {
  id: string;
  icon: HistoryIcon;
  title: string;
  at: string | null;
  atKind: "date" | "datetime";
  datePrefix?: string;
  actorPersonId?: string | null;
  actorPersonName?: string;
};

export type HistoryRequisition = {
  openedDate?: string | null;
  guardrailEvaluation?: GuardrailEvaluation | null;
};

const text = (value: string | null | undefined): string => value?.trim() ?? "";

const ordered = (steps: ApprovalStep[] | null | undefined): ApprovalStep[] =>
  [...(steps ?? [])].sort((left, right) => left.order - right.order);

const groupName = (group: ApprovalGroup): string =>
  text(group.approverGroupName) || "Approver group";

const personName = (group: ApprovalGroup): string =>
  text(group.personName) || "Unknown";

const addedBy = (group: ApprovalGroup): string => {
  const names = (group.addingRules ?? [])
    .map((rule) => text(rule.ruleName) || text(rule.ruleId))
    .filter((name) => name !== "");
  if (names.length === 0) {
    return "";
  }
  return ` (added by rule: ${names.join(", ")})`;
};

const passedCount = (evaluation: GuardrailEvaluation): number =>
  evaluation.checks.filter((check) => check.state === "PASS").length;

const warningCount = (evaluation: GuardrailEvaluation): number =>
  evaluation.checks.filter((check) => {
    if (check.state === "PASS") {
      return false;
    }
    return (check.effect ?? "BLOCK") === "WARN";
  }).length;

const evaluatedTitle = (evaluation: GuardrailEvaluation): string => {
  const passed = passedCount(evaluation);
  const warnings = warningCount(evaluation);
  const warningWord = warnings === 1 ? "warning" : "warnings";
  return `Guardrails evaluated — ${passed} passed, ${warnings} ${warningWord}`;
};

const firstGroupNames = (steps: ApprovalStep[]): string => {
  const step = steps.find((item) => item.kind === "group");
  if (!step) {
    return "";
  }
  return (step.groups ?? []).map(groupName).join(", ");
};

const routedTitle = (
  evaluation: GuardrailEvaluation,
  steps: ApprovalStep[]
): string | null => {
  switch (evaluation.template) {
    case "exception": {
      const names = failingCheckLabels(evaluation.checks).join(", ");
      return names
        ? `Routed for approval — exceptions: ${names}`
        : "Routed for approval — exceptions";
    }
    case "fast-track":
      return "Fast-track route";
    case "standard": {
      const names = firstGroupNames(steps);
      return names ? `Routed for approval — ${names}` : "Routed for approval";
    }
    default:
      return null;
  }
};

const actedTitle = (group: ApprovalGroup): string | null => {
  const extra = addedBy(group);
  switch (group.status) {
    case "APPROVED":
      return `Approved by ${personName(group)} as ${groupName(group)}${extra}`;
    case "REFUSED": {
      const reasons = (group.reasons ?? [])
        .map((reason) => text(reason.label))
        .filter((label) => label !== "")
        .join(", ");
      const comment = text(group.comment);
      let title = `Refused by ${personName(group)} as ${groupName(group)}`;
      if (reasons) {
        title += ` — ${reasons}`;
      }
      if (comment) {
        title += `. ${comment}`;
      }
      return `${title}${extra}`;
    }
    default:
      return null;
  }
};

const waitingTitle = (group: ApprovalGroup): string => {
  let title = `Waiting for ${groupName(group)}`;
  if (typeof group.slaHours === "number" && Number.isFinite(group.slaHours)) {
    title += ` (SLA ${group.slaHours} h)`;
  }
  if (group.pastSla === true) {
    title += " · Past SLA";
  }
  return title;
};

/**
 * Oldest first, in chain order. A reason row has no date until the API
 * exposes actedAt on the reason step.
 * ponytail: one latest stamp. A re-evaluation replaces earlier rounds.
 */
export const guardrailHistory = (
  requisition: HistoryRequisition
): HistoryRow[] => {
  const rows: HistoryRow[] = [
    {
      id: "created",
      icon: "file",
      title: "Request created",
      at: text(requisition.openedDate) || null,
      atKind: "date",
    },
  ];
  const evaluation = requisition.guardrailEvaluation;
  if (!evaluation) {
    return rows;
  }
  const steps = ordered(evaluation.steps);
  const evaluatedAt = text(evaluation.evaluatedAt) || null;
  if (evaluatedAt) {
    rows.push({
      id: "evaluated",
      icon: "clipboard-check",
      title: evaluatedTitle(evaluation),
      at: evaluatedAt,
      atKind: "datetime",
    });
  }
  const routed = routedTitle(evaluation, steps);
  if (routed) {
    rows.push({
      id: "routed",
      icon: "paper-plane",
      title: routed,
      at: evaluatedAt,
      atKind: "datetime",
    });
  }
  const waiting: HistoryRow[] = [];
  for (const step of steps) {
    if (step.kind === "reason" && text(step.reason) !== "") {
      rows.push({
        id: `reason-${step.order}`,
        icon: "comment",
        title: "Exception reason saved",
        at: text(step.actedAt) || null,
        atKind: "datetime",
      });
    }
    if (step.kind !== "group") {
      continue;
    }
    for (const group of step.groups ?? []) {
      const title = actedTitle(group);
      if (title) {
        rows.push({
          id: `acted-${step.order}-${group.approverGroupId}`,
          icon: group.status === "REFUSED" ? "xmark" : "check",
          title,
          at: text(group.actedAt) || null,
          atKind: "datetime",
          actorPersonId: text(group.personId) || null,
          actorPersonName: personName(group),
        });
      }
      if (group.status === "PENDING" && text(group.clockStartedAt) !== "") {
        waiting.push({
          id: `waiting-${step.order}-${group.approverGroupId}`,
          icon: "clock",
          title: waitingTitle(group),
          at: text(group.clockStartedAt),
          atKind: "datetime",
          datePrefix: "since",
        });
      }
    }
  }
  return [...rows, ...waiting];
};
